import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import DateInput from '../components/DateInput';
import ProRataHelper from '../components/ProRataHelper';
import { useAuth } from '../context/AuthContext';
import StarterSection, { StarterProgress } from '../components/starter/StarterSection';
import { PersonalFields, AddressFields, BankFields, StarterDocumentFields } from '../components/starter/StarterFields';
import { validatePersonal, validateAddress, validateBank, scrollToFirstError, firstSectionWithError, errorSummary, normaliseStarterFields, validateFiles } from '../utils/starterValidation';

function fmtGBP(val) {
  return '£' + (parseFloat(val) || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const initialForm = {
  // Required fields
  first_name: '', last_name: '', email: '', gender: '',
  date_of_birth: '', address_line_1: '', address_line_2: '', city: '', postcode: '',
  start_date: '', job_title: '', annual_salary: '',
  is_director: false,
  // Only asked when is_director is ticked. See DIRECTOR_NI_OPTIONS.
  director_ni_method: '', director_appointed_date: '',
  // Employment
  contract_type: 'permanent', contract_end_date: '',
  employment_type: 'full-time', part_time_basis: '',
  prorata_salary: '', pt_days_per_week: '', pt_hours_per_day: '',
  pt_weekly_hours_custom: '', pt_fte_weekly_hours: '37.5',
  pay_frequency: 'monthly',
  ni_number: '',
  // Pro-rata method override (NULL = inherit company default at read time)
  prorata_method: '', prorata_custom_divisor: '',
  // Pay type (hourly support)
  pay_type: 'salaried', hourly_rate: '', contracted_hours_per_week: '',
  // Bank details (required for payroll)
  bank_account_name: '', sort_code: '', account_number: '',
  // Pension
  pension_enroll: 'defer', deferral_months: 3,
  // Why this starter differs from the company's recorded postponement policy.
  // Only required (and only sent) when the choice actually contradicts it.
  pension_policy_override_reason: '',
  pension_earnings_basis: 'qualifying',
  pension_employer_type: 'percentage', pension_employer_value: 3,
  pension_employee_type: 'percentage', pension_employee_value: 5,
  // Scheme type — defaults to the company default (loaded on mount); admin/client
  // can override per starter. Empty string = inherit the company default on save.
  pension_scheme_type: '',
  // P45/Starter checklist
  has_p45: 'no',
};


// Which fields sit in which section. A collapsed section renders nothing, so
// without this a failed submit could flag a field that is nowhere on screen and
// the button would look dead.
const SECTION_FIELDS = [
  ['first_name', 'last_name', 'email', 'gender', 'date_of_birth', 'ni_number'],
  ['address_line_1', 'address_line_2', 'city', 'postcode'],
  ['start_date', 'job_title', 'contract_type', 'contract_end_date', 'employment_type',
    'director_ni_method', 'director_appointed_date', 'prorata_salary', 'pt_days_per_week', 'pt_hours_per_day', 'pt_weekly_hours_custom', 'pt_fte_weekly_hours'],
  ['annual_salary', 'hourly_rate', 'contracted_hours_per_week', 'pay_frequency'],
  ['bank_account_name', 'sort_code', 'account_number'],
  ['pension_enroll', 'pension_employer_value', 'pension_employee_value', 'pension_scheme_type', 'pension_policy_override_reason'],
  ['documents'],
];

// Directors' NI can be worked out two ways; both give the same total for the tax
// year and differ only in when it is paid. BrightPay needs to be told which.
const DIRECTOR_NI_OPTIONS = [
  {
    value: 'alternative',
    label: 'Alternative method',
    tag: 'Most common',
    desc: 'NI is worked out each payday, like any other employee, then squared up at the end of the tax year. Payslips stay steady month to month.',
  },
  {
    value: 'annual',
    label: 'Annual method',
    tag: "HMRC's default",
    desc: 'NI is worked out on their total pay for the whole tax year. Often no NI is taken for several months, then it all comes out once pay passes the yearly threshold.',
  },
  {
    value: 'unsure',
    label: "Not sure — let my accountant decide",
    desc: "We'll pick the right one and let you know.",
  },
];

const SCHEME_LABELS = {
  salary_sacrifice: 'Salary Sacrifice',
  net_pay: 'Net Pay',
  relief_at_source: 'Relief at Source',
};

// Map an ISO/date string to the UK tax (year-month) key it falls in — matching the
// backend, which treats a payroll period as a whole calendar month
// (tax_month 1 = April … 12 = March). Used to warn when a start date lands in a
// period the client has already submitted.
function startDateTaxKey(dateStr) {
  if (!dateStr) return null;
  let y;
  let m;
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
  if (iso) { y = +iso[1]; m = +iso[2]; }
  else {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return null;
    y = d.getFullYear();
    m = d.getMonth() + 1;
  }
  const taxYear = m >= 4 ? y : y - 1;
  const taxMonth = m >= 4 ? m - 3 : m + 9;
  return `${taxYear}-${taxMonth}`;
}

function monthYearLabel(dateStr) {
  if (!dateStr) return '';
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
  const d = iso ? new Date(Date.UTC(+iso[1], +iso[2] - 1, +iso[3])) : new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

export default function NewStarterPage() {
  const { user } = useAuth();
  const hourlyTrackingEnabled = user?.hourly_tracking_enabled || false;
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [submitError, setSubmitError] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editChangeId, setEditChangeId] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Fill it in here, or email the personal half to the new starter. Editing an
  // existing change is always the full form — there is nobody left to send it to.
  const [sendMode, setSendMode] = useState(searchParams.get('mode') === 'send');
  const [sendResult, setSendResult] = useState(null);
  const [triedSubmit, setTriedSubmit] = useState(false);

  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId) {
      setEditMode(true);
      setEditChangeId(editId);
      // Load existing pending change data
      api.get('/pending-changes').then(res => {
        const change = res.data.find(c => String(c.id) === String(editId));
        if (change) {
          const data = typeof change.change_data === 'string' ? JSON.parse(change.change_data) : change.change_data;
          setForm(prev => ({
            ...prev,
            ...data,
            has_p45: data.has_p45 || 'no',
          }));
        }
      }).catch(err => {
        console.error('Failed to load change for editing:', err);
      });
    }
  }, [searchParams]);

  // Closed payroll periods (already submitted/processed) as a Set of "taxYear-taxMonth"
  // keys, so we can warn the client if they enter a start date in a month that's
  // already been run — that starter can't join a closed run and will be handled as
  // back-pay in the next one.
  const [closedPeriodKeys, setClosedPeriodKeys] = useState(() => new Set());
  useEffect(() => {
    let cancelled = false;
    api.get('/submissions/periods')
      .then(res => {
        if (cancelled) return;
        const keys = new Set(
          (res.data || [])
            .filter(p => p.status === 'submitted' || p.status === 'processed')
            .map(p => `${p.tax_year}-${p.tax_month}`)
        );
        setClosedPeriodKeys(keys);
      })
      .catch(() => { /* non-fatal — warning just won't show */ });
    return () => { cancelled = true; };
  }, []);

  const startInClosedPeriod = useMemo(() => {
    const key = startDateTaxKey(form.start_date);
    return !!key && closedPeriodKeys.has(key);
  }, [form.start_date, closedPeriodKeys]);

  // Company default pension scheme — new starters inherit it unless overridden.
  // 'none' means the company runs no scheme, so there's nothing to pre-fill.
  const [companyDefaultScheme, setCompanyDefaultScheme] = useState('');
  // The company's recorded postponement policy: null = none recorded (behave as
  // before and flag nothing), 0 = enrol from day one, 1-3 = postpone that long.
  const [pensionPolicyMonths, setPensionPolicyMonths] = useState(null);
  useEffect(() => {
    const companyId = user?.company_id;
    if (!companyId) return;
    let cancelled = false;
    api.get(`/pensions/company/${companyId}/scheme-default`)
      .then(res => {
        if (cancelled) return;
        const def = res.data?.default_pension_scheme_type || '';
        setCompanyDefaultScheme(def);
        // Pre-fill the starter's scheme with the company default, but never
        // overwrite a value the user already picked or one loaded for editing.
        if (def && def !== 'none') {
          setForm(prev => prev.pension_scheme_type ? prev : { ...prev, pension_scheme_type: def });
        }

        const policy = res.data?.pension_postponement_months;
        const months = policy === null || policy === undefined ? null : parseInt(policy, 10);
        if (months === null || Number.isNaN(months)) return;
        setPensionPolicyMonths(months);
        // Seed the choice from the policy so the common case needs no thought at
        // all. Never in edit mode — an existing starter's stored choice wins.
        if (!editMode) {
          setForm(prev => months === 0
            ? { ...prev, pension_enroll: 'immediate' }
            : { ...prev, pension_enroll: 'defer', deferral_months: months });
        }
      })
      .catch(() => { /* non-fatal — fall back to "not set" */ });
    return () => { cancelled = true; };
  }, [user?.company_id, editMode]);

  // Which of the two choices the recorded policy points at, so the right answer is
  // marked before anyone picks rather than only corrected afterwards.
  const policyOption = pensionPolicyMonths === null
    ? null
    : (pensionPolicyMonths === 0 ? 'immediate' : 'defer');

  // Does the current choice contradict the recorded policy? Mirrors
  // evaluatePensionPolicy on the backend, which is what actually enforces it.
  const pensionPolicyBreach = useMemo(() => {
    if (pensionPolicyMonths === null) return null;
    let chosenMonths;
    if (form.pension_enroll === 'immediate') chosenMonths = 0;
    else if (form.pension_enroll === 'defer') chosenMonths = parseInt(form.deferral_months, 10) || 3;
    else return null;
    if (chosenMonths === pensionPolicyMonths) return null;
    const describe = (m) => (m === 0 ? 'enrol immediately' : `postpone ${m} month${m === 1 ? '' : 's'}`);
    return { expected: describe(pensionPolicyMonths), chosen: describe(chosenMonths) };
  }, [pensionPolicyMonths, form.pension_enroll, form.deferral_months]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
      // Switching away from fixed-term hides the end date field, so clear the value
      // too — otherwise a date typed before the switch is still in state and would
      // be submitted against a permanent contract.
      if (name === 'contract_type' && value !== 'fixed-term') next.contract_end_date = '';
      if (name === 'is_director' && !checked) {
        next.director_ni_method = '';
        next.director_appointed_date = '';
      }
      return next;
    });
    setErrors(prev => ({
      ...prev,
      [name]: null,
      ...(name === 'contract_type' && value !== 'fixed-term' ? { contract_end_date: null } : {}),
    }));
  };

  // Part-time salary calculation (mirrors ContractualChangeModal logic)
  const [ptHoursMode, setPtHoursMode] = useState('days'); // 'days' or 'custom'

  const ptWeeklyHoursFromDays = useMemo(() => {
    const days = parseFloat(form.pt_days_per_week) || 0;
    const hpd = parseFloat(form.pt_hours_per_day) || 0;
    return days * hpd;
  }, [form.pt_days_per_week, form.pt_hours_per_day]);

  const ptEffectiveWeeklyHours = ptHoursMode === 'days'
    ? ptWeeklyHoursFromDays
    : parseFloat(form.pt_weekly_hours_custom) || 0;

  const ptFteHours = parseFloat(form.pt_fte_weekly_hours) || 0;

  const calculatedProRata = useMemo(() => {
    const annual = parseFloat(form.annual_salary) || 0;
    if (annual <= 0 || form.employment_type !== 'part-time') return '';

    if (ptHoursMode === 'days') {
      const days = parseFloat(form.pt_days_per_week) || 0;
      if (days > 0 && days <= 5) {
        return (annual * (days / 5)).toFixed(2);
      }
    } else {
      const custom = parseFloat(form.pt_weekly_hours_custom) || 0;
      const fte = parseFloat(form.pt_fte_weekly_hours) || 0;
      if (custom > 0 && fte > 0) {
        return (annual * (custom / fte)).toFixed(2);
      }
    }
    return '';
  }, [form.annual_salary, form.employment_type, form.pt_days_per_week, form.pt_weekly_hours_custom, form.pt_fte_weekly_hours, ptHoursMode]);

  // The share of full-time this pattern is, on the same basis as the calculation above.
  const ptFraction = useMemo(() => {
    if (ptHoursMode === 'days') {
      const days = parseFloat(form.pt_days_per_week) || 0;
      return days > 0 && days <= 5 ? days / 5 : 0;
    }
    const custom = parseFloat(form.pt_weekly_hours_custom) || 0;
    const fte = parseFloat(form.pt_fte_weekly_hours) || 0;
    return custom > 0 && fte > 0 && custom < fte ? custom / fte : 0;
  }, [ptHoursMode, form.pt_days_per_week, form.pt_weekly_hours_custom, form.pt_fte_weekly_hours]);

  // The client has typed over the calculated pro-rata salary. Payroll pays from the
  // annual (full-time) salary scaled by the hours, not from this box, so the two
  // must agree. The usual cause is the client's real salary entered in both boxes.
  const proRataMismatch = form.employment_type === 'part-time'
    && !!calculatedProRata
    && parseFloat(form.prorata_salary) > 0
    && Math.abs(parseFloat(form.prorata_salary) - parseFloat(calculatedProRata)) > 1;

  // "The figure I typed is what they'll be paid": work the full-time salary back
  // from it, and the calculation then fills the pro-rata box with the same figure.
  const useProRataAsActualPay = () => {
    const actual = parseFloat(form.prorata_salary);
    if (!(actual > 0) || !(ptFraction > 0)) return;
    setForm(prev => ({ ...prev, annual_salary: (actual / ptFraction).toFixed(2) }));
    setErrors(prev => ({ ...prev, prorata_salary: undefined }));
  };

  // "The annual salary is the full-time salary": put the calculated figure back.
  const useAnnualAsFullTime = () => {
    setForm(prev => ({ ...prev, prorata_salary: calculatedProRata }));
    setErrors(prev => ({ ...prev, prorata_salary: undefined }));
  };

  // Auto-populate prorata_salary from calculation
  useEffect(() => {
    if (calculatedProRata && form.employment_type === 'part-time') {
      setForm(prev => ({ ...prev, prorata_salary: calculatedProRata }));
    }
  }, [calculatedProRata, form.employment_type]);

  // Compute the assessment date from start_date + deferral_months for display
  const computedAssessmentDate = form.start_date && form.deferral_months
    ? (() => {
        const d = new Date(form.start_date);
        d.setMonth(d.getMonth() + form.deferral_months);
        return d.toLocaleDateString('en-GB');
      })()
    : '';

  const validate = () => {
    const errs = {};

    // The half the employer always fills in, whichever route they take.
    if (!form.first_name.trim()) errs.first_name = 'Required';
    if (!form.last_name.trim()) errs.last_name = 'Required';
    if (!form.email.trim()) errs.email = sendMode ? "Required — this is where we'll send the form" : 'Required for payslip delivery';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Invalid email format';
    if (!form.start_date) errs.start_date = 'Required';

    if (form.pay_type === 'hourly') {
      if (!form.hourly_rate || parseFloat(form.hourly_rate) <= 0) errs.hourly_rate = 'Must be greater than 0';
      if (form.contracted_hours_per_week === '' || form.contracted_hours_per_week === undefined) errs.contracted_hours_per_week = 'Required (enter 0 for zero-hours contract)';
      if (form.contracted_hours_per_week !== '' && parseFloat(form.contracted_hours_per_week) < 0) errs.contracted_hours_per_week = 'Cannot be negative';
    } else {
      if (!form.annual_salary || parseFloat(form.annual_salary) <= 0) errs.annual_salary = 'Must be greater than 0';
    }

    // The new starter's own details. Sending them the form is precisely the
    // case where the employer doesn't have these, so don't ask for them.
    if (!sendMode) {
      Object.assign(errs, validatePersonal(form), validateAddress(form), validateBank(form));
    }

    // Fixed-term validation — the end date is the whole point of a fixed-term
    // contract. Without it the payroll team has no idea when the contract runs out
    // and the auto-leaver prompt on the run sheet never fires.
    if (form.is_director) {
      if (!form.director_ni_method) errs.director_ni_method = 'Choose how their NI should be worked out';
    }

    if (form.contract_type === 'fixed-term') {
      if (!form.contract_end_date) {
        errs.contract_end_date = 'Required for a fixed-term contract';
      } else if (form.start_date && form.contract_end_date <= form.start_date) {
        errs.contract_end_date = 'Must be after the start date';
      }
    }

    // Part-time validation
    if (form.employment_type === 'part-time') {
      if (!form.prorata_salary || parseFloat(form.prorata_salary) <= 0) errs.prorata_salary = 'Required for part-time contracts';
      else if (proRataMismatch) errs.prorata_salary = 'Doesn\'t match the hours — choose which figure is right below';
      // Validate working pattern fields based on mode
      if (ptHoursMode === 'days') {
        if (!form.pt_days_per_week || parseFloat(form.pt_days_per_week) <= 0) errs.pt_days_per_week = 'Required';
        if (parseFloat(form.pt_days_per_week) > 7) errs.pt_days_per_week = 'Cannot exceed 7';
        if (!form.pt_hours_per_day || parseFloat(form.pt_hours_per_day) <= 0) errs.pt_hours_per_day = 'Required';
        if (parseFloat(form.pt_hours_per_day) > 24) errs.pt_hours_per_day = 'Cannot exceed 24';
      } else {
        if (!form.pt_weekly_hours_custom || parseFloat(form.pt_weekly_hours_custom) <= 0) errs.pt_weekly_hours_custom = 'Required';
        if (!form.pt_fte_weekly_hours || parseFloat(form.pt_fte_weekly_hours) <= 0) errs.pt_fte_weekly_hours = 'Required';
      }
    }

    // Going against the client's own recorded pension policy is allowed — genuine
    // exceptions exist — but it has to be explained, so the payroll team gets a
    // reason rather than a surprise. The backend enforces this too.
    if (pensionPolicyBreach && !(form.pension_policy_override_reason || '').trim()) {
      errs.pension_policy_override_reason = 'Please say why this starter differs from the company policy';
    }

    // Document upload required - must have P45 or starter checklist (not required
    // in edit mode as docs may already exist, nor when the new starter is the one
    // being asked for it).
    if (!editMode && !sendMode && uploadFiles.length === 0) {
      errs.documents = form.has_p45 === 'yes' ? 'P45 document is required' : 'New Starter Checklist is required';
    }

    setErrors(errs);
    setTriedSubmit(true);
    if (Object.keys(errs).length === 0) return true;

    // Move to the section holding the first problem before scrolling to it —
    // a field in a collapsed section has nowhere to display its error, which
    // makes a failed submit look like a dead button.
    const failed = firstSectionWithError(errs, SECTION_FIELDS);
    if (failed !== null && visibleSteps.includes(failed)) setActiveStep(failed);
    scrollToFirstError();
    return false;
  };

  // The employment half of a new starter, shaped for the API. Shared by both
  // routes so a starter the employee completes is built the same way as one
  // the client types in full.
  const buildChangeData = () => {
    const changeData = normaliseStarterFields(form);
    delete changeData.has_p45;
    delete changeData.account_number_confirm;

    // Ensure numeric fields are stored as proper rounded numbers
    if (changeData.annual_salary) changeData.annual_salary = parseFloat(parseFloat(changeData.annual_salary).toFixed(2));
    if (changeData.prorata_salary) changeData.prorata_salary = parseFloat(parseFloat(changeData.prorata_salary).toFixed(2));
    if (changeData.hourly_rate) changeData.hourly_rate = parseFloat(parseFloat(changeData.hourly_rate).toFixed(2));
    if (changeData.contracted_hours_per_week) changeData.contracted_hours_per_week = parseFloat(parseFloat(changeData.contracted_hours_per_week).toFixed(2));
    if (changeData.pension_employer_value) changeData.pension_employer_value = parseFloat(parseFloat(changeData.pension_employer_value).toFixed(2));
    if (changeData.pension_employee_value) changeData.pension_employee_value = parseFloat(parseFloat(changeData.pension_employee_value).toFixed(2));

    // Clean up pay-type-specific fields
    if (changeData.pay_type === 'hourly') {
      delete changeData.annual_salary;
      delete changeData.prorata_salary;
    } else {
      delete changeData.hourly_rate;
      delete changeData.contracted_hours_per_week;
    }

    // The override reason only means something alongside a real deviation. Without
    // this a stale reason could ride along on a starter that matches policy fine.
    if (!pensionPolicyBreach) {
      delete changeData.pension_policy_override_reason;
    }

    // Remove pension detail fields when deferring (they're not relevant)
    if (changeData.pension_enroll === 'defer') {
      delete changeData.pension_earnings_basis;
      delete changeData.pension_employer_type;
      delete changeData.pension_employer_value;
      delete changeData.pension_employee_type;
      delete changeData.pension_employee_value;
      delete changeData.pension_scheme_type;
    } else {
      // Conversely, a deferral period only means something when deferring. The form
      // seeds deferral_months to its default, so without this it would ride along on
      // an immediate enrolment and read as a contradictory instruction to the admin
      // reviewing the change.
      delete changeData.deferral_months;
    }

    // Remove fields that don't apply based on selections
    if (!changeData.is_director) {
      delete changeData.director_ni_method;
      delete changeData.director_appointed_date;
    } else if (!changeData.director_appointed_date) {
      delete changeData.director_appointed_date;
    }
    if (changeData.contract_type !== 'fixed-term') {
      delete changeData.contract_end_date;
    }
    if (changeData.employment_type !== 'part-time') {
      delete changeData.part_time_basis;
      delete changeData.prorata_salary;
      delete changeData.pt_days_per_week;
      delete changeData.pt_hours_per_day;
      delete changeData.pt_weekly_hours_custom;
      delete changeData.pt_fte_weekly_hours;
    } else {
      // Store the hours mode and effective weekly hours for reference
      changeData.pt_hours_mode = ptHoursMode;
      changeData.pt_weekly_hours = ptEffectiveWeeklyHours.toFixed(1);
      // Build a readable part_time_basis description if not manually provided
      if (!changeData.part_time_basis || !changeData.part_time_basis.trim()) {
        if (ptHoursMode === 'days') {
          changeData.part_time_basis = `${form.pt_days_per_week} days per week, ${form.pt_hours_per_day} hours per day (${ptWeeklyHoursFromDays.toFixed(1)} hours/week)`;
        } else {
          changeData.part_time_basis = `${form.pt_weekly_hours_custom} hours per week (FTE: ${form.pt_fte_weekly_hours} hours)`;
        }
      }
      // Clean up mode-specific fields
      if (ptHoursMode === 'days') {
        delete changeData.pt_weekly_hours_custom;
        delete changeData.pt_fte_weekly_hours;
      } else {
        delete changeData.pt_days_per_week;
        delete changeData.pt_hours_per_day;
      }
    }
    return changeData;
  };

  // Email the form to the new starter instead of filling it in. Only the
  // employment half travels — the employee's own fields are theirs to supply,
  // and the API rejects them here anyway.
  const handleSend = async () => {
    setSubmitting(true);
    setSubmitError('');
    try {
      const payload = buildChangeData();
      for (const field of [
        'gender', 'date_of_birth', 'ni_number',
        'address_line_1', 'address_line_2', 'city', 'postcode',
        'bank_account_name', 'sort_code', 'account_number',
      ]) delete payload[field];

      const res = await api.post('/starter-invites', {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        ...payload,
      });
      setSendResult(res.data);
      // Clear the form behind the confirmation so sending another starter is a
      // fresh sheet, not the last one's details waiting to be edited.
      setForm(initialForm);
      setActiveStep(0);
      setErrors({});
      try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setSubmitError('Could not send the form: ' + (err.response?.data?.error || err.message));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    if (sendMode) return handleSend();

    setSubmitting(true);
    try {
      const changeData = buildChangeData();

      if (editMode && editChangeId) {
        // Update existing pending change
        await api.patch(`/pending-changes/${editChangeId}`, { change_data: changeData });

        // Upload new documents if provided
        if (uploadFiles.length > 0) {
          // Get the employee_id from the pending change
          const changesRes = await api.get('/pending-changes');
          const change = changesRes.data.find(c => String(c.id) === String(editChangeId));
          if (change && change.employee_id) {
            const formData = new FormData();
            for (const file of uploadFiles) {
              formData.append('documents', file);
            }
            formData.append('document_type', form.has_p45 === 'yes' ? 'p45' : 'starter_checklist');
            await api.post(`/documents/${change.employee_id}`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
          }
        }
      } else {
        const res = await api.post('/pending-changes', {
          change_type: 'new_starter',
          change_data: changeData,
        });

        // Upload documents if provided
        if (uploadFiles.length > 0 && res.data.employee_id) {
          const formData = new FormData();
          for (const file of uploadFiles) {
            formData.append('documents', file);
          }
          formData.append('document_type', form.has_p45 === 'yes' ? 'p45' : 'starter_checklist');
          await api.post(`/documents/${res.data.employee_id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        }
      }

      try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
      navigate('/');
    } catch (err) {
      setSubmitError('Failed to submit: ' + (err.response?.data?.error || err.message));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileChange = (e) => {
    const chosen = Array.from(e.target.files);
    const fileError = validateFiles(chosen);
    if (fileError) {
      setUploadFiles([]);
      setErrors(prev => ({ ...prev, documents: fileError }));
      e.target.value = '';
      return;
    }
    setUploadFiles(chosen);
    setErrors(prev => ({ ...prev, documents: null }));
  };

  const inputClass = (field) =>
    `mt-1 block w-full rounded-md shadow-sm focus:ring-brand-500 focus:border-brand-500 ${
      errors[field] ? 'border-red-300' : 'border-gray-300'
    }`;

  // Section completion for the progress strip — 7 steps matching the 7 cards in render order
  const sectionDone = [
    sendMode
      ? !!form.first_name && !!form.last_name && !!form.email
      : !!form.first_name && !!form.last_name && !!form.email && !!form.gender && !!form.date_of_birth && !!form.ni_number,
    !!form.address_line_1 && !!form.city && !!form.postcode,
    !!form.start_date && !!form.contract_type && (form.contract_type !== 'fixed-term' || !!form.contract_end_date) && !!form.employment_type && !!form.pay_frequency
      && (!form.is_director || !!form.director_ni_method),
    form.pay_type === 'hourly' ? !!form.hourly_rate : !!form.annual_salary,
    !!form.bank_account_name && !!form.sort_code && !!form.account_number,
    !!form.pension_enroll && (form.pension_enroll === 'defer' || (!!form.pension_employer_value && !!form.pension_employee_value))
      && (!pensionPolicyBreach || !!(form.pension_policy_override_reason || '').trim()),
    !!form.has_p45,
  ];
  const sectionLabels = ['Personal', 'Address', 'Employment', 'Pay', 'Bank', 'Pension', 'Checklist'];
  const sectionDescs = [
    'Name, contact, identity',
    'Home address',
    'Role, contract, hours',
    'Salary or hourly rate',
    'Where their pay lands',
    'Workplace pension choice',
    'P45 or new starter info',
  ];
  // Which sections this run of the form actually shows. Sending the form to the
  // employee drops the four sections they will fill in themselves — the same
  // JSX, just not rendered — so the employment, pay and pension fields cannot
  // drift between the two flows.
  const SEND_STEPS = [0, 2, 3, 5];
  const ALL_STEPS = [0, 1, 2, 3, 4, 5, 6];
  const visibleSteps = sendMode ? SEND_STEPS : ALL_STEPS;

  const [activeStep, setActiveStep] = useState(0);
  const goPrev = () => setActiveStep(s => {
    const i = visibleSteps.indexOf(s);
    return i > 0 ? visibleSteps[i - 1] : s;
  });
  const goNext = () => setActiveStep(s => {
    const i = visibleSteps.indexOf(s);
    return i < visibleSteps.length - 1 ? visibleSteps[i + 1] : s;
  });

  const doneCount = visibleSteps.filter(i => sectionDone[i]).length;

  const sectionProps = (id) => ({
    step: visibleSteps.indexOf(id),
    total: visibleSteps.length,
    label: sendMode && id === 0 ? 'Who' : sectionLabels[id],
    desc: sendMode && id === 0 ? 'Who they are, and where to send the form' : sectionDescs[id],
    active: activeStep === id,
    onPrev: goPrev,
    onNext: goNext,
  });

  // Save & continue later — persist the in-progress form to localStorage.
  // The edit-mode flow has its own ?edit= load; we never want to merge a stale
  // local draft on top of that, so the restore reads the query string directly
  // and bails out when editing an existing pending change.
  const draftKey = `sa_new_starter_draft_${user?.id || 'anon'}`;
  const [savedAt, setSavedAt] = useState(null);
  useEffect(() => {
    if (searchParams.get('edit')) return;
    try {
      const saved = localStorage.getItem(draftKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === 'object') setForm(prev => ({ ...prev, ...parsed }));
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleSaveDraft = () => {
    try {
      localStorage.setItem(draftKey, JSON.stringify(form));
      setSavedAt(new Date());
      setTimeout(() => setSavedAt(null), 4000);
    } catch { /* ignore */ }
  };

  return (
    <div className="max-w-[920px] mx-auto px-8 py-7">
      <button onClick={() => navigate('/')} className="text-[13px] text-gray-500 hover:text-brand-700 inline-flex items-center gap-1 mb-3">
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
        Back to dashboard
      </button>

      {sendResult && (
        <div className="mb-6 bg-white dark:bg-gray-800 rounded-2xl ring-1 ring-emerald-200 dark:ring-emerald-800 overflow-hidden">
          <div className="px-6 py-5">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 grid place-items-center w-8 h-8 rounded-full bg-emerald-100 shrink-0">
                <svg className="w-4 h-4 text-emerald-700" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </span>
              <div>
                <h2 className="font-heading text-[18px] text-ink dark:text-gray-100 leading-tight">
                  Form sent to {sendResult.first_name}
                </h2>
                <p className="text-[13.5px] text-gray-600 dark:text-gray-400 mt-1.5 leading-relaxed">
                  We've emailed {sendResult.email} asking for their address, NI number, bank details and P45.
                  When they're done they'll go straight into your payroll changes and we'll email you — there's
                  nothing for you to approve. We'll chase them once if they don't reply.
                </p>
              </div>
            </div>
          </div>
          <div className="px-6 py-3 bg-gray-50/60 dark:bg-gray-900/30 border-t border-gray-100 dark:border-gray-700 flex flex-wrap gap-2 justify-end">
            <button type="button" onClick={() => navigate('/')}
              className="px-4 py-2 text-[13px] font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200">
              Back to dashboard
            </button>
            <button type="button"
              onClick={() => setSendResult(null)}
              className="px-4 py-2 text-[13px] font-semibold text-white bg-brand-600 rounded-md hover:bg-brand-700">
              Send another
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div>
          <p className="text-[11.5px] uppercase tracking-[0.14em] font-bold text-brand-700">
            {editMode ? 'Edit new starter' : 'Add a new starter'}
          </p>
          <h1 className="font-heading text-[28px] leading-tight text-ink dark:text-gray-100 mt-1">Who's joining the team?</h1>
          <p className="text-[14px] text-gray-500 dark:text-gray-400 mt-1.5 max-w-[640px]">
            {sendMode
              ? "Set the job and the pay, and we'll email them for the rest — their address, NI number, bank details and P45."
              : 'Fill the sections below — required fields are marked with *. Your changes are queued in the next submission.'}
          </p>
        </div>
        <div className="text-right">
          <p className="text-[11.5px] uppercase tracking-[0.14em] font-bold text-gray-500">Progress</p>
          <p className="text-[14px] font-semibold text-ink mt-1 tabular">{doneCount} of {visibleSteps.length} sections</p>
        </div>
      </header>

      {/* How this starter gets added. Hidden when editing an existing change —
          it has already been added, and there is nobody left to send a form to. */}
      {!editMode && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
          {[
            {
              value: true,
              title: 'Send the form to them',
              body: "You add the job and the pay. They add their address, NI number, bank details and P45, and go into your payroll changes when they're done.",
              foot: "Best when you don't have their P45 or bank details",
            },
            {
              value: false,
              title: "I'll fill it in myself",
              body: 'The full form. You will need their NI number, bank details and their P45 or starter checklist to hand.',
              foot: 'Fastest when you already have everything',
            },
          ].map(opt => {
            const selected = sendMode === opt.value;
            return (
              <button type="button" key={opt.title}
                onClick={() => { setSendMode(opt.value); setActiveStep(0); setErrors({}); }}
                className={`text-left bg-white dark:bg-gray-800 rounded-2xl overflow-hidden transition-shadow ${
                  selected ? 'ring-2 ring-brand-500' : 'ring-1 ring-gray-200 dark:ring-gray-700 hover:ring-gray-300'
                }`}>
                <div className="px-5 py-4">
                  <div className="flex items-start gap-3">
                    <span className={`mt-0.5 grid place-items-center w-5 h-5 rounded-full shrink-0 ${
                      selected ? 'bg-brand-600' : 'ring-1 ring-gray-300 bg-white dark:bg-gray-800'
                    }`}>
                      {selected && <span className="w-2 h-2 rounded-full bg-white block" />}
                    </span>
                    <div>
                      <p className="font-heading text-[15px] text-ink dark:text-gray-100 leading-tight">{opt.title}</p>
                      <p className="text-[12.5px] text-gray-500 dark:text-gray-400 mt-1 leading-snug">{opt.body}</p>
                    </div>
                  </div>
                </div>
                <div className={`px-5 py-2 border-t ${
                  selected ? 'bg-brand-50 dark:bg-brand-900/20 border-brand-100 dark:border-brand-800' : 'bg-gray-50 dark:bg-gray-900/40 border-gray-100 dark:border-gray-700'
                }`}>
                  <p className={`text-[11.5px] font-semibold ${selected ? 'text-brand-700' : 'text-gray-500'}`}>{opt.foot}</p>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <StarterProgress
        labels={visibleSteps.map(i => (sendMode && i === 0 ? 'Who' : sectionLabels[i]))}
        done={visibleSteps.map(i => sectionDone[i])}
        activeStep={visibleSteps.indexOf(activeStep)}
        onJump={(i) => setActiveStep(visibleSteps[i])}
      />

      {submitError && (
        <div className="mb-4 p-3 bg-rose-50 border border-rose-200 rounded text-sm text-rose-700">
          {submitError}
        </div>
      )}

      {triedSubmit && Object.keys(errors).length > 0 && (
        <div className="mb-4 p-3 bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded">
          <p className="text-sm font-semibold text-rose-800 dark:text-rose-200">
            {Object.keys(errors).length === 1
              ? 'One thing to fix before this can be saved'
              : `${Object.keys(errors).length} things to fix before this can be saved`}
          </p>
          <ul className="mt-1.5 space-y-0.5">
            {errorSummary(errors).map(item => (
              <li key={item.field} className="text-[13px] text-rose-700 dark:text-rose-300">
                <button type="button"
                  onClick={() => {
                    const target = firstSectionWithError({ [item.field]: item.message }, SECTION_FIELDS);
                    if (target !== null && visibleSteps.includes(target)) setActiveStep(target);
                  }}
                  className="underline underline-offset-2 hover:no-underline text-left">
                  {item.label}
                </button>
                {' — '}{item.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Personal Details */}
        <StarterSection {...sectionProps(0)}>
          {sendMode ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="send-first-name">First Name *</label>
                <input id="send-first-name" name="first_name" maxLength={100} value={form.first_name} onChange={handleChange} className={inputClass('first_name')} />
                {errors.first_name && <p className="text-red-500 text-xs mt-1">{errors.first_name}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="send-last-name">Last Name *</label>
                <input id="send-last-name" name="last_name" maxLength={100} value={form.last_name} onChange={handleChange} className={inputClass('last_name')} />
                {errors.last_name && <p className="text-red-500 text-xs mt-1">{errors.last_name}</p>}
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="send-email">Their email address *</label>
                <input id="send-email" name="email" type="email" value={form.email} onChange={handleChange} className={inputClass('email')} />
                {errors.email
                  ? <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                  : <p className="text-xs text-gray-500 mt-1">We'll send the form here. A personal address is usually safer — they may not have a work account on day one.</p>}
              </div>
            </div>
          ) : (
            <PersonalFields form={form} errors={errors} onChange={handleChange} inputClass={inputClass} />
          )}
        </StarterSection>

        {/* Address */}
        <StarterSection {...sectionProps(1)}>
          <AddressFields form={form} errors={errors} onChange={handleChange} inputClass={inputClass} />
        </StarterSection>

        {/* Employment Details */}
        <StarterSection {...sectionProps(2)}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Start Date *</label>
              <DateInput name="start_date" value={form.start_date} onChange={handleChange} className={inputClass('start_date')} />
              {errors.start_date && <p className="text-red-500 text-xs mt-1">{errors.start_date}</p>}
              {startInClosedPeriod && (
                <div className="mt-2 rounded-md bg-amber-50 dark:bg-amber-900/20 ring-1 ring-amber-300 dark:ring-amber-700 px-3 py-2 text-[13px] text-amber-800 dark:text-amber-200 leading-snug">
                  <span className="font-semibold">Heads up:</span> you've already submitted payroll for {monthYearLabel(form.start_date)}. That run is closed, so this starter can't be added to it. They'll go into your next payroll run and be flagged for your payroll team to sort out any back-pay owed from this start date.
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Job Title</label>
              <input name="job_title" value={form.job_title} onChange={handleChange} className={inputClass('job_title')} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Contract Type</label>
              <select name="contract_type" value={form.contract_type} onChange={handleChange} className={inputClass('contract_type')}>
                <option value="permanent">Permanent</option>
                <option value="fixed-term">Fixed Term</option>
              </select>
            </div>
            {form.contract_type === 'fixed-term' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Contract End Date *</label>
                <DateInput name="contract_end_date" value={form.contract_end_date} onChange={handleChange} className={inputClass('contract_end_date')} />
                {errors.contract_end_date ? (
                  <p className="text-red-500 text-xs mt-1">{errors.contract_end_date}</p>
                ) : (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">The date the contract runs out, so we know when to process them as a leaver.</p>
                )}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Employment Type</label>
              <select name="employment_type" value={form.employment_type} onChange={handleChange} className={inputClass('employment_type')}>
                <option value="full-time">Full Time</option>
                <option value="part-time">Part Time</option>
              </select>
            </div>
            {form.employment_type === 'part-time' && (
              <div className="md:col-span-2 space-y-4">
                <div className="p-4 bg-gray-50 dark:bg-gray-900/40 rounded-lg space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Working Pattern *</p>
                    <div className="flex rounded-md border border-gray-300 dark:border-gray-700 overflow-hidden">
                      <button type="button" onClick={() => setPtHoursMode('days')}
                        className={`px-3 py-1 text-xs font-medium transition-colors ${
                          ptHoursMode === 'days' ? 'bg-brand-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}>
                        Days &amp; Hours
                      </button>
                      <button type="button" onClick={() => setPtHoursMode('custom')}
                        className={`px-3 py-1 text-xs font-medium transition-colors border-l border-gray-300 dark:border-gray-700 ${
                          ptHoursMode === 'custom' ? 'bg-brand-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}>
                        Weekly Hours
                      </button>
                    </div>
                  </div>

                  {ptHoursMode === 'days' ? (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Days per Week *</label>
                          <input type="number" name="pt_days_per_week" step="0.5" min="0.5" max="7" inputMode="decimal"
                            value={form.pt_days_per_week} onChange={handleChange}
                            className={inputClass('pt_days_per_week')} placeholder="e.g. 3" />
                          {errors.pt_days_per_week && <p className="text-red-500 text-xs mt-1">{errors.pt_days_per_week}</p>}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Hours per Day *</label>
                          <input type="number" name="pt_hours_per_day" step="0.25" min="0.25" max="24" inputMode="decimal"
                            value={form.pt_hours_per_day} onChange={handleChange}
                            className={inputClass('pt_hours_per_day')} placeholder="e.g. 7.5" />
                          {errors.pt_hours_per_day && <p className="text-red-500 text-xs mt-1">{errors.pt_hours_per_day}</p>}
                        </div>
                      </div>
                      {ptWeeklyHoursFromDays > 0 && (
                        <div className="text-sm text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 rounded p-3 border border-gray-200 dark:border-gray-700">
                          <span className="font-medium">Weekly hours:</span> {ptWeeklyHoursFromDays.toFixed(1)} hours
                          ({form.pt_days_per_week} day{parseFloat(form.pt_days_per_week) !== 1 ? 's' : ''} &times; {form.pt_hours_per_day} hrs/day)
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <p className="text-xs text-gray-500">
                        Use this when the working pattern doesn't fit neatly into fixed days.
                      </p>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">New Weekly Hours *</label>
                          <input type="number" name="pt_weekly_hours_custom" step="0.5" min="0.5" max="168" inputMode="decimal"
                            value={form.pt_weekly_hours_custom} onChange={handleChange}
                            className={inputClass('pt_weekly_hours_custom')} placeholder="e.g. 20" />
                          {errors.pt_weekly_hours_custom && <p className="text-red-500 text-xs mt-1">{errors.pt_weekly_hours_custom}</p>}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">FT Weekly Hours *</label>
                          <input type="number" name="pt_fte_weekly_hours" step="0.5" min="0.5" max="168" inputMode="decimal"
                            value={form.pt_fte_weekly_hours} onChange={handleChange}
                            className={inputClass('pt_fte_weekly_hours')} placeholder="e.g. 37.5" />
                          <p className="text-xs text-gray-500 mt-1">Standard full-time hours for this role</p>
                        </div>
                      </div>
                      {ptEffectiveWeeklyHours > 0 && ptFteHours > 0 && (
                        <div className="text-sm text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 rounded p-3 border border-gray-200 dark:border-gray-700">
                          <span className="font-medium">{ptEffectiveWeeklyHours.toFixed(1)}</span> of <span className="font-medium">{ptFteHours.toFixed(1)}</span> hours
                          ({((ptEffectiveWeeklyHours / ptFteHours) * 100).toFixed(0)}% of full-time)
                        </div>
                      )}
                    </>
                  )}

                  {calculatedProRata && (
                    <div className="text-sm text-brand-700 bg-brand-50 rounded p-3 border border-brand-200">
                      <span className="font-medium">Calculated pro-rata salary:</span> &pound;{parseFloat(calculatedProRata).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      <span className="block text-xs mt-1 text-brand-600">
                        {ptHoursMode === 'days'
                          ? `Based on ${form.pt_days_per_week} of 5 days (${((parseFloat(form.pt_days_per_week) / 5) * 100).toFixed(0)}% of FTE)`
                          : `Based on ${form.pt_weekly_hours_custom} of ${form.pt_fte_weekly_hours} hours (${((ptEffectiveWeeklyHours / ptFteHours) * 100).toFixed(0)}% of FTE)`
                        }
                      </span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Additional Notes</label>
                  <textarea name="part_time_basis" value={form.part_time_basis} onChange={handleChange}
                    rows="2"
                    placeholder="Any additional details about the working pattern (optional)"
                    className={inputClass('part_time_basis')} />
                </div>
              </div>
            )}
            <div>
              <label className="flex items-center space-x-2 mt-6">
                <input name="is_director" type="checkbox" checked={form.is_director} onChange={handleChange}
                  className="rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
                <span className="text-sm font-medium text-gray-700">Is a Director</span>
              </label>
            </div>
            {form.is_director && (
              <div className="md:col-span-2 rounded-lg ring-1 ring-brand-200 bg-brand-50/40 dark:bg-gray-800 dark:ring-gray-600 p-4 space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200">How should we work out this director's National Insurance? *</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Both methods give the same total NI for the tax year. They only change <em>when</em> it's paid.
                  </p>
                  <div className="mt-3 space-y-2" role="radiogroup" aria-label="Director NI method">
                    {DIRECTOR_NI_OPTIONS.map(opt => (
                      <label
                        key={opt.value}
                        className={`flex items-start gap-3 rounded-md p-3 ring-1 cursor-pointer bg-white dark:bg-gray-700 ${form.director_ni_method === opt.value ? 'ring-brand-500' : 'ring-gray-200 dark:ring-gray-600'}`}
                      >
                        <input
                          type="radio"
                          name="director_ni_method"
                          value={opt.value}
                          checked={form.director_ni_method === opt.value}
                          onChange={handleChange}
                          className="mt-1 text-brand-600 focus:ring-brand-500"
                        />
                        <span>
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{opt.label}</span>
                          {opt.tag && <span className="ml-2 text-[11px] font-semibold uppercase tracking-wide text-brand-700 bg-brand-100 rounded px-1.5 py-0.5">{opt.tag}</span>}
                          <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">{opt.desc}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                  {errors.director_ni_method && <p className="text-red-500 text-xs mt-1">{errors.director_ni_method}</p>}
                </div>
                <div className="max-w-xs">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Date appointed as director</label>
                  <DateInput name="director_appointed_date" value={form.director_appointed_date} onChange={handleChange} className={inputClass('director_appointed_date')} />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    If they became a director partway through the tax year, their NI is worked out from this date. Leave blank if you're not sure.
                  </p>
                </div>
              </div>
            )}
          </div>
        </StarterSection>

        {/* Compensation */}
        <StarterSection {...sectionProps(3)}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {hourlyTrackingEnabled && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Pay Type *</label>
                <div className="flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden w-fit">
                  <button type="button" onClick={() => setForm(prev => ({ ...prev, pay_type: 'salaried' }))}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                      form.pay_type === 'salaried' ? 'bg-brand-600 text-white' : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}>
                    Salaried
                  </button>
                  <button type="button" onClick={() => setForm(prev => ({ ...prev, pay_type: 'hourly' }))}
                    className={`px-4 py-2 text-sm font-medium transition-colors border-l border-gray-300 dark:border-gray-600 ${
                      form.pay_type === 'hourly' ? 'bg-brand-600 text-white' : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}>
                    Hourly
                  </button>
                </div>
              </div>
            )}
            {form.pay_type === 'hourly' ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Hourly Rate (GBP) *</label>
                  <div className="mt-1 relative">
                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400 text-sm">&pound;</span>
                    <input name="hourly_rate" type="number" step="0.01" min="0" inputMode="decimal" value={form.hourly_rate} onChange={handleChange}
                      className={`block w-full pl-7 ${inputClass('hourly_rate')}`} placeholder="e.g. 12.50" />
                  </div>
                  {errors.hourly_rate && <p className="text-red-500 text-xs mt-1">{errors.hourly_rate}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Contracted Hours per Week *</label>
                  <input name="contracted_hours_per_week" type="number" step="0.5" min="0" max="168" inputMode="decimal" value={form.contracted_hours_per_week} onChange={handleChange}
                    className={inputClass('contracted_hours_per_week')} placeholder="e.g. 37.5 (0 for zero-hours)" />
                  {errors.contracted_hours_per_week && <p className="text-red-500 text-xs mt-1">{errors.contracted_hours_per_week}</p>}
                  <p className="text-xs text-gray-500 mt-1">Enter 0 for zero-hours contracts. Used for pension calculations and holiday entitlement.</p>
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {form.employment_type === 'part-time' ? 'Full-Time Annual Salary (GBP) *' : 'Annual Salary (GBP) *'}
                  </label>
                  {form.employment_type === 'part-time' && (
                    <p className="text-xs text-gray-500">What they would earn working full-time hours. Their actual pay is worked out from this below.</p>
                  )}
                  <input name="annual_salary" type="number" step="any" min="0" inputMode="decimal" value={form.annual_salary} onChange={handleChange} className={inputClass('annual_salary')} />
                  {errors.annual_salary && <p className="text-red-500 text-xs mt-1">{errors.annual_salary}</p>}
                </div>
                {form.start_date && form.annual_salary && (
                  <ProRataHelper
                    annualSalary={form.annual_salary}
                    employmentStart={form.start_date}
                    initialMethod={form.prorata_method || undefined}
                    label="First-month pro-rata pay"
                    onApply={(result) => {
                      setForm(prev => ({
                        ...prev,
                        prorata_method: result.method,
                        prorata_custom_divisor: result.method === 'custom' ? String(result.totalDays) : '',
                      }));
                    }}
                  />
                )}
                {form.employment_type === 'part-time' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Pro-Rata Salary (GBP) *
                      {calculatedProRata && <span className="text-xs font-normal text-gray-400 ml-1">(auto-calculated)</span>}
                    </label>
                    <input name="prorata_salary" type="number" step="any" min="0" inputMode="decimal" value={form.prorata_salary} onChange={handleChange}
                      placeholder="Calculated from working pattern above"
                      className={inputClass('prorata_salary')} />
                    {errors.prorata_salary && <p className="text-red-500 text-xs mt-1">{errors.prorata_salary}</p>}
                    {!calculatedProRata && form.employment_type === 'part-time' && (
                      <p className="text-xs text-gray-500 mt-1">Enter the working pattern above to auto-calculate, or enter manually</p>
                    )}
                    {proRataMismatch && (
                      <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                        <p>
                          For {ptHoursMode === 'days'
                            ? `${form.pt_days_per_week} of 5 days`
                            : `${form.pt_weekly_hours_custom} of ${form.pt_fte_weekly_hours} hours`},
                          a full-time salary of {fmtGBP(form.annual_salary)} works out
                          at {fmtGBP(calculatedProRata)}, not {fmtGBP(form.prorata_salary)}.
                          Which is right?
                        </p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          <button type="button" onClick={useProRataAsActualPay}
                            className="px-3 py-1.5 rounded-md bg-white ring-1 ring-amber-300 font-medium hover:bg-amber-100">
                            {fmtGBP(form.prorata_salary)} is what they'll actually be paid
                          </button>
                          <button type="button" onClick={useAnnualAsFullTime}
                            className="px-3 py-1.5 rounded-md bg-white ring-1 ring-amber-300 font-medium hover:bg-amber-100">
                            {fmtGBP(form.annual_salary)} is the full-time salary
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Pay Frequency</label>
              <select name="pay_frequency" value={form.pay_frequency} onChange={handleChange} className={inputClass('pay_frequency')}>
                <option value="monthly">Monthly</option>
                <option value="weekly">Weekly</option>
                <option value="4-weekly">4-Weekly</option>
              </select>
            </div>
          </div>
        </StarterSection>

        {/* Bank Details */}
        <StarterSection {...sectionProps(4)}>
          <BankFields form={form} errors={errors} onChange={handleChange} inputClass={inputClass} />
        </StarterSection>

        {/* Pension Deferral */}
        <StarterSection {...sectionProps(5)}>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { value: 'immediate', label: 'Enrol immediately', desc: 'Contributions start from the first pay run.' },
                { value: 'defer',     label: 'Defer assessment',  desc: 'Postpone the assessment up to 3 months.' },
              ].map((opt) => {
                const selected = form.pension_enroll === opt.value;
                return (
                  <button key={opt.value} type="button"
                    onClick={() => setForm(prev => ({ ...prev, pension_enroll: opt.value }))}
                    className={`text-left rounded-lg ring-1 p-4 transition ${selected ? 'bg-brand-50 ring-brand-300 dark:bg-brand-900/30 dark:ring-brand-700 shadow-sm' : 'bg-white dark:bg-gray-800 ring-gray-200 dark:ring-gray-700 hover:ring-brand-200'}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`grid place-items-center w-4 h-4 rounded-full ring-1 ${selected ? 'bg-brand-600 ring-brand-600' : 'bg-white dark:bg-gray-700 ring-gray-300 dark:ring-gray-500'}`}>
                        {selected && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                      </span>
                      <p className={`text-[14px] font-semibold ${selected ? 'text-brand-800' : 'text-ink'}`}>{opt.label}</p>
                      {policyOption === opt.value && (
                        <span className="text-[11px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                          Your policy
                        </span>
                      )}
                    </div>
                    <p className="text-[12.5px] text-gray-500 mt-1.5 ml-6">{opt.desc}</p>
                  </button>
                );
              })}
            </div>

            {pensionPolicyBreach && (
              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded space-y-3">
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                  <div>
                    <p className="text-[14px] font-semibold text-amber-900 dark:text-amber-200">
                      This differs from your company&apos;s usual pension policy
                    </p>
                    <p className="text-[13px] text-amber-800 dark:text-amber-300 mt-1">
                      Your standard policy is to <span className="font-semibold">{pensionPolicyBreach.expected}</span>,
                      but this starter is set to <span className="font-semibold">{pensionPolicyBreach.chosen}</span>.
                      You can still go ahead — just tell us why so the payroll team knows it&apos;s deliberate.
                    </p>
                  </div>
                </div>
                <div>
                  <label htmlFor="pension_policy_override_reason" className="block text-sm font-medium text-amber-900 dark:text-amber-200 mb-1">
                    Reason for the difference <span className="text-red-600">*</span>
                  </label>
                  <textarea
                    id="pension_policy_override_reason"
                    name="pension_policy_override_reason"
                    rows={2}
                    value={form.pension_policy_override_reason || ''}
                    onChange={handleChange}
                    placeholder="e.g. Director — already enrolled in a scheme elsewhere"
                    className={`block w-full rounded-md shadow-sm focus:ring-brand-500 focus:border-brand-500 text-[14px] ${errors.pension_policy_override_reason ? 'border-red-300' : 'border-gray-300'}`}
                  />
                  {errors.pension_policy_override_reason && (
                    <p className="mt-1 text-[12.5px] text-red-600">{errors.pension_policy_override_reason}</p>
                  )}
                </div>
              </div>
            )}

            {form.pension_enroll === 'defer' && (
              <div className="ml-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Deferral Period</label>
                  <div className="space-y-2">
                    {[1, 2, 3].map(months => (
                      <label key={months} className="flex items-center space-x-2">
                        <input type="radio" name="deferral_months" value={months}
                          checked={form.deferral_months === months}
                          onChange={() => setForm(prev => ({ ...prev, deferral_months: months }))}
                          className="text-brand-600 focus:ring-brand-500" />
                        <span className="text-sm text-gray-700">{months} month{months > 1 ? 's' : ''}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded px-3 py-2 mt-3">
                    The maximum postponement period allowed by law is 3 months from the employee's start date.
                  </p>
                  {computedAssessmentDate && (
                    <p className="text-xs text-gray-500 mt-2">
                      Assessment due by: <span className="font-medium">{computedAssessmentDate}</span>
                    </p>
                  )}
                </div>
              </div>
            )}

            {form.pension_enroll === 'immediate' && (
              <div className="ml-6 p-4 bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 rounded space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Scheme Type</label>
                  <select name="pension_scheme_type" value={form.pension_scheme_type} onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-brand-500 focus:border-brand-500">
                    <option value="">— not set —</option>
                    <option value="salary_sacrifice">Salary Sacrifice (pre-tax)</option>
                    <option value="net_pay">Net Pay (pre-tax)</option>
                    <option value="relief_at_source">Relief at Source (post-tax)</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {companyDefaultScheme && companyDefaultScheme !== 'none'
                      ? <>Defaulted from the company scheme ({SCHEME_LABELS[companyDefaultScheme] || companyDefaultScheme}). Change it only if this starter differs.</>
                      : <>No company default scheme is set — pick one here, or set a company default so future starters inherit it. Leaving this unset will flag on the run sheet.</>}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Earnings Basis</label>
                  <select name="pension_earnings_basis" value={form.pension_earnings_basis} onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-brand-500 focus:border-brand-500">
                    <option value="qualifying">Qualifying Earnings</option>
                    <option value="full">Full Earnings</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Employer Type</label>
                    <select name="pension_employer_type" value={form.pension_employer_type} onChange={handleChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm">
                      <option value="percentage">Percentage</option>
                      <option value="fixed-amount">Fixed Amount</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Employer Value</label>
                    <input name="pension_employer_value" type="number" step="0.01" min="0" inputMode="decimal"
                      value={form.pension_employer_value} onChange={handleChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Employee Type</label>
                    <select name="pension_employee_type" value={form.pension_employee_type} onChange={handleChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm">
                      <option value="percentage">Percentage</option>
                      <option value="fixed-amount">Fixed Amount</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Employee Value</label>
                    <input name="pension_employee_value" type="number" step="0.01" min="0" inputMode="decimal"
                      value={form.pension_employee_value} onChange={handleChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
                  </div>
                </div>
                {/* Live monthly contribution preview */}
                {(parseFloat(form.pension_employer_value) > 0 || parseFloat(form.pension_employee_value) > 0) && (() => {
                  const annual = parseFloat(form.annual_salary) || 0;
                  // Part-time staff contribute on their pro-rata pay, not the FTE salary.
                  const proRata = parseFloat(form.prorata_salary) || 0;
                  const pensionBase = (form.employment_type === 'part-time' && proRata > 0) ? proRata : annual;
                  const monthlyG = pensionBase / 12;
                  const erValRaw = parseFloat(form.pension_employer_value) || 0;
                  const eeValRaw = parseFloat(form.pension_employee_value) || 0;
                  const erAmt = form.pension_employer_type === 'percentage' ? (monthlyG * erValRaw / 100) : erValRaw;
                  const eeAmt = form.pension_employee_type === 'percentage' ? (monthlyG * eeValRaw / 100) : eeValRaw;
                  const totalAmt = erAmt + eeAmt;
                  const gbp = (n) => n.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' });
                  return (
                    <div className="mt-4 bg-gradient-to-br from-brand-50 to-brand-100/60 ring-1 ring-brand-200 rounded-lg p-4">
                      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                        <p className="text-[11px] uppercase tracking-wider font-bold text-brand-700">Monthly contribution preview</p>
                        {pensionBase > 0 && <p className="text-[11.5px] text-brand-700/80">Based on {gbp(pensionBase)} {(form.employment_type === 'part-time' && proRata > 0) ? 'pro-rata salary' : 'annual salary'}</p>}
                      </div>
                      <div className="grid grid-cols-3 gap-3 text-center">
                        <div>
                          <p className="text-[11px] uppercase tracking-wider text-gray-500">Employer</p>
                          <p className="font-heading text-[18px] tabular text-brand-800 mt-0.5">{gbp(erAmt)}</p>
                        </div>
                        <div>
                          <p className="text-[11px] uppercase tracking-wider text-gray-500">Employee</p>
                          <p className="font-heading text-[18px] tabular text-brand-800 mt-0.5">{gbp(eeAmt)}</p>
                        </div>
                        <div>
                          <p className="text-[11px] uppercase tracking-wider text-gray-500">Total</p>
                          <p className="font-heading text-[22px] font-bold tabular text-brand-900 mt-0.5">{gbp(totalAmt)}</p>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </StarterSection>

        {/* P45 / Starter Checklist */}
        <StarterSection {...sectionProps(6)}>
          <StarterDocumentFields
            form={form}
            errors={errors}
            files={uploadFiles}
            onToggleP45={(val) => setForm(prev => ({ ...prev, has_p45: val }))}
            onFileChange={handleFileChange}
          />
        </StarterSection>

        {/* Submit */}
        <div className="flex flex-wrap items-center justify-end gap-3">
          {savedAt && (
            <span className="text-[12.5px] text-emerald-700 mr-auto">
              Draft saved {savedAt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          {!sendMode && (
            <button type="button" onClick={handleSaveDraft}
              className="px-4 py-2 text-[13px] font-medium text-brand-700 ring-1 ring-brand-200 bg-white rounded-md hover:bg-brand-50">
              Save &amp; continue later
            </button>
          )}
          <button type="button" onClick={() => navigate('/')}
            className="px-4 py-2 text-[13px] font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">
            Cancel
          </button>
          <button type="submit" disabled={submitting}
            className="px-5 py-2 text-[13px] font-semibold text-white bg-brand-600 rounded-md hover:bg-brand-700 disabled:opacity-50">
            {sendMode
              ? (submitting ? 'Sending…' : `Send form to ${form.first_name || 'them'} →`)
              : (submitting ? (editMode ? 'Saving…' : 'Adding…') : (editMode ? 'Save changes' : 'Add new starter →'))}
          </button>
        </div>
      </form>
    </div>
  );
}
